/**
 * Coin Actions Panel (coin detail sub-section):
 * - Manipulation (선동) 3 buttons: cost / cooldown / backfire odds, targets this coin
 * - Whale wallet tracker (meme coins, always visible): ad-gated live phase
 *   reveal for 3 min, 90s cooldown
 * - Emergency averaging-down fund: ad-gated cash grant at deep loss
 */
import { getCoinConfig } from '../game/config/coins.js';
import { MANIPULATIONS } from '../game/config/market.js';
import { RESCUE_CONFIG } from '../game/infoActions.js';
import { getCurrentCPS } from '../game/state.js';
import { getPnlPercent } from '../game/systems/tradingSystem.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';
import { getState, dispatch, setInnerHTML, markScreenDirty, requestRender, showToast } from './renderer.js';
import { panicHookHtml, insuranceHtml, rewindHookHtml, bindRiskHooks } from './riskHooksPanel.js';

const shownHooks = new Set<string>();

function logShownOnce(hookId: string, key: string) {
  if (shownHooks.has(key)) return;
  shownHooks.add(key);
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId, event: 'shown' } });
}

function renderManipSection(coinId: string): string {
  const st = getState();
  const now = Date.now();
  const cps = getCurrentCPS(st);
  const active = st.market.activeManipulation;
  const activeHere = active && active.coinId === coinId && now < active.expiresAt;

  let html = `<div class="cd-section-title">${t('cd_manip_title')}</div>`;
  if (activeHere) {
    const rem = Math.ceil((active.expiresAt - now) / 1000);
    html += `<div class="cd-manip-active">${t('cd_manip_active')} ${rem}s</div>`;
  }
  html += '<div class="cd-manip-row">';
  for (const m of MANIPULATIONS) {
    const cost = Math.max(m.minCost, cps * m.costMultiplier);
    const cdUntil = st.market.manipulationCooldowns[m.id] || 0;
    const onCooldown = now < cdUntil;
    const anyActive = active && now < active.expiresAt;
    const disabled = onCooldown || !!anyActive || st.coins < cost;
    const sub = onCooldown
      ? `${Math.ceil((cdUntil - now) / 1000)}s`
      : `${formatNumber(cost)} · ${t('cd_manip_backfire')} ${Math.round(m.backfireChance * 100)}%`;
    html += `<button class="cd-manip-btn${disabled ? ' cd-manip-disabled' : ''}" data-manip="${m.id}" ${disabled ? 'disabled' : ''}>
      <span class="manip-label">${t('manip_' + m.id)}</span>
      <span class="manip-cost">${sub}</span>
    </button>`;
  }
  return html + '</div>';
}

function renderWhaleSection(coinId: string): string {
  const cfg = getCoinConfig(coinId);
  const whale = getState().whales[coinId];
  if (!cfg || cfg.tier !== 'meme' || !whale) return '';
  const now = Date.now();

  if (now < whale.trackerRevealUntil) {
    const rem = Math.ceil((whale.trackerRevealUntil - now) / 1000);
    return `<div class="cd-whale-reveal">
      <span class="cd-whale-label">${t('whale_phase_label')}</span>
      <span class="cd-whale-phase cd-whale-${whale.phase}">${t('whale_phase_' + whale.phase)}</span>
      <span class="cd-whale-timer">${rem}s</span>
    </div>`;
  }
  if (now < whale.trackerCooldownUntil) {
    const rem = Math.ceil((whale.trackerCooldownUntil - now) / 1000);
    return `<button class="hook-btn hook-btn-disabled" disabled>
      <span class="hook-main">${t('whale_tracker_btn')}</span>
      <span class="hook-sub">${t('rescue_cooldown')} ${rem}s</span>
    </button>`;
  }
  logShownOnce('whale_tracker', `whale_tracker:${coinId}`);
  return `<button class="hook-btn" data-hook="whale">
    <span class="hook-main">${t('whale_tracker_btn')}</span>
    <span class="hook-sub">${t('whale_tracker_sub')}</span>
  </button>`;
}

/**
 * Rescue hook button HTML - shared between coin detail and the exchange
 * holdings card. Empty string when not eligible; compact hides the cooldown row.
 */
export function rescueHookHtml(coinId: string, compact = false): string {
  const st = getState();
  const holding = st.exchange.holdings[coinId];
  const coin = st.exchange.coins[coinId];
  if (!holding || !coin) return '';
  if (getPnlPercent(holding, coin.price) > RESCUE_CONFIG.lossThresholdPct) return '';

  const now = Date.now();
  const cdUntil = st.rescueCooldowns[coinId] ?? 0;
  if (now < cdUntil) {
    if (compact) return '';
    const rem = Math.ceil((cdUntil - now) / 1000);
    return `<div class="cd-rescue-cd">${t('rescue_btn')} · ${t('rescue_cooldown')} ${rem}s</div>`;
  }
  logShownOnce('rescue_funds', `rescue_funds:${coinId}`);
  const grant = holding.amount * coin.price * RESCUE_CONFIG.grantRatio;
  return `<button class="hook-btn hook-btn-rescue${compact ? ' hook-btn-sm' : ''}" data-hook="rescue" data-rescue-coin="${coinId}">
    <span class="hook-main">${t('rescue_btn')} +${formatNumber(grant)}</span>
    <span class="hook-sub">${t('rescue_sub')}</span>
  </button>`;
}

function onManipClick(coinId: string, manipId: string) {
  dispatch({ type: 'MARKET_MANIPULATE', payload: { manipulationId: manipId, coinId } });
  const active = getState().market.activeManipulation;
  if (active?.id === manipId && active.coinId === coinId) {
    playSfx('purchase');
    showToast(t('cd_manip_success'));
  } else {
    playSfx('error');
  }
  markScreenDirty();
  requestRender();
}

async function onWhaleTrack(coinId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'whale_tracker', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().WHALE_TRACKER);
  if (result) {
    dispatch({ type: 'WHALE_TRACK', payload: { coinId } });
    playSfx('achievement');
  }
  markScreenDirty();
  requestRender();
}

/** Ad flow for the rescue hook (exported for the exchange holdings card). */
export async function runRescueHook(coinId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'rescue_funds', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().RESCUE_FUNDS);
  if (result) {
    dispatch({ type: 'RESCUE_FUNDS', payload: { coinId } });
    playSfx('golden');
    showToast(t('rescue_received'));
  }
  markScreenDirty();
  requestRender();
}

/** Render into the given container and (re)bind events when HTML changed. */
export function renderCoinActions(container: HTMLElement, coinId: string) {
  // data-coin wrapper guarantees a rebind when switching coins with identical sections
  const html = `<div class="cd-actions-inner" data-coin="${coinId}">`
    + panicHookHtml(coinId)
    + insuranceHtml(coinId)
    + rewindHookHtml()
    + rescueHookHtml(coinId)
    + renderManipSection(coinId)
    + renderWhaleSection(coinId)
    + '</div>';

  if (!setInnerHTML(container, html)) return;

  container.querySelectorAll('[data-manip]').forEach(btn => {
    btn.addEventListener('click', () => onManipClick(coinId, (btn as HTMLElement).dataset.manip!));
  });
  container.querySelector('[data-hook="whale"]')?.addEventListener('click', () => void onWhaleTrack(coinId));
  container.querySelector('[data-hook="rescue"]')?.addEventListener('click', () => void runRescueHook(coinId));
  bindRiskHooks(container);
}
